import Hammer from "hammerjs";
import ProductListGalleryContent from "./ProductListGalleryContent";
import elementService from "@/common/ElementService";

export default new class ProductListGalleryService {
    public init(): void {
        for (const galleryContainer of document.querySelectorAll<HTMLDivElement>(".js-desktop-gallery")) {
            this.initGallery(
                new ProductListGalleryContent(
                    galleryContainer,
                    this.extractImageList(galleryContainer),
                    this.extractMobileImageList(galleryContainer),
                ),
            );
        }
    }

    private initGallery(productListGalleryContent: ProductListGalleryContent): void {
        this.initDesktopImageSwitch(productListGalleryContent);
        this.initMobileImageSwitch(productListGalleryContent);
        this.initMobileSwipe(productListGalleryContent);
    }

    private initDesktopImageSwitch(productListGalleryContent: ProductListGalleryContent): void {
        for (const imageSwitch of productListGalleryContent.getGalleryContainer().querySelectorAll<HTMLLIElement>(".js-gallery-image")) {
            imageSwitch.addEventListener("click", () => this.changeImageBySwitch(productListGalleryContent, imageSwitch));
        }
    }

    private initMobileImageSwitch(productListGalleryContent: ProductListGalleryContent): void {
        for (const imageSwitch of productListGalleryContent.getGalleryContainer().querySelectorAll<HTMLLIElement>(".js-mobile-gallery-image")) {
            imageSwitch.addEventListener("click", () => this.changeImageBySwitch(productListGalleryContent, imageSwitch));
        }
    }

    private initMobileSwipe(productListGalleryContent: ProductListGalleryContent): void {
        const listGalleryImageContainer = this.findListGalleryImageContainer(productListGalleryContent);

        if (listGalleryImageContainer === null) {
            return;
        }

        const listGalleryHammer = new Hammer(listGalleryImageContainer);

        listGalleryHammer.on("swipeleft", () => this.switchToRightImage(productListGalleryContent));
        listGalleryHammer.on("swiperight", () => this.switchToLeftImage(productListGalleryContent));
    }

    private findListGalleryImageContainer(productListGalleryContent: ProductListGalleryContent): HTMLDivElement | null {
        return productListGalleryContent.getGalleryContainer().querySelector<HTMLDivElement>(".js-list-gallery-image-container");
    }

    private changeImageBySwitch(productListGalleryContent: ProductListGalleryContent, imageSwitch: HTMLLIElement): void {
        this.switchToImage(productListGalleryContent, this.getElementSourceImageUrl(imageSwitch));
    }

    private switchToLeftImage(productListGalleryContent: ProductListGalleryContent): void {
        this.switchToImage(
            productListGalleryContent,
            productListGalleryContent.getMobileImageUrlByIndex(
                (productListGalleryContent.getShownImageIndex() + productListGalleryContent.getImagesCount() - 1) % productListGalleryContent.getImagesCount(),
            ),
        );
    }

    private switchToRightImage(productListGalleryContent: ProductListGalleryContent): void {
        this.switchToImage(
            productListGalleryContent,
            productListGalleryContent.getMobileImageUrlByIndex((productListGalleryContent.getShownImageIndex() + 1) % productListGalleryContent.getImagesCount()),
        );
    }

    private switchToImage(productListGalleryContent: ProductListGalleryContent, imageUrl: string): void {
        this.toggleActiveSwitch(productListGalleryContent.getGalleryContainer().querySelectorAll<HTMLLIElement>(".js-gallery-image"), imageUrl);
        this.toggleActiveSwitch(productListGalleryContent.getGalleryContainer().querySelectorAll<HTMLLIElement>(".js-mobile-gallery-image"), imageUrl);
        this.getMainImage(productListGalleryContent).src = imageUrl;
        productListGalleryContent.setShownImageUrl(imageUrl);
        this.updateImageGalleryCounter(productListGalleryContent);
        this.updateSelectedImageIndex(productListGalleryContent);
    }

    private toggleActiveSwitch(imageGallerySwitches: NodeListOf<HTMLLIElement>, imageUrl: string): void {
        for (let index = 0; index < imageGallerySwitches.length; index++) {
            const liElement = imageGallerySwitches.item(index);

            if (!liElement.classList.contains("js-image-control-more")) {
                liElement.classList.toggle("active", this.getElementSourceImageUrl(liElement) === imageUrl);
            }
        }
    }

    private getElementSourceImageUrl(liElement: HTMLLIElement): string {
        return elementService.elementData(liElement, "srcPopup");
    }

    private getMainImage(productListGalleryContent: ProductListGalleryContent): HTMLImageElement {
        return elementService.elementQuerySelector<HTMLImageElement>(productListGalleryContent.getGalleryContainer(), ".image-caption > .label-product-inner > .img-responsive");
    }

    private extractImageList(galleryContainer: HTMLDivElement): string[] {
        return [...galleryContainer.querySelectorAll<HTMLLIElement>(".js-gallery-image")]
            .map((liElement: HTMLLIElement) => this.getElementSourceImageUrl(liElement));
    }

    private extractMobileImageList(galleryContainer: HTMLDivElement): string[] {
        return [...galleryContainer.querySelectorAll<HTMLLIElement>(".js-mobile-gallery-image")]
            .map((liElement: HTMLLIElement) => this.getElementSourceImageUrl(liElement));
    }

    private updateImageGalleryCounter(productListGalleryContent: ProductListGalleryContent): void {
        const imageGalleryCounter = elementService.elementQuerySelector<HTMLSpanElement>(productListGalleryContent.getGalleryContainer(), ".js-image-gallery-counter");

        imageGalleryCounter.textContent = `${productListGalleryContent.getShownImageIndex() + 1}/${productListGalleryContent.getImagesCount()}`;
    }

    private updateSelectedImageIndex(productListGalleryContent: ProductListGalleryContent): void {
        const listGalleryImageContainer = this.findListGalleryImageContainer(productListGalleryContent);

        if (listGalleryImageContainer === null) {
            return;
        }

        listGalleryImageContainer.dataset.imageIndex = String(productListGalleryContent.getShownImageIndex());
    }
}();
